import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import InfoIcon from 'components/material-symbol/icons/Info'
import TipWell from 'components/tip-well'
import Typography from '@material-ui/core/Typography'
import FileUploadIcon from 'components/material-symbol/icons/FileUpload'
import SendIcon from 'components/material-symbol/icons/Send'
import VideoFilePicker from 'components/video-file-picker'
import { MAX_VIDEO_SIZE_MB } from 'lib/cloudinary/upload-video'
import { useSnackbar } from 'lib/snackbar'
import { getErrorMessage, useSubmitVirtualCoachingVideos } from './api-hooks'
import {
  trackVirtualCoachingStudentEvent,
  VIRTUAL_COACHING_STUDENT_EVENTS,
} from './analytics'
import { useStyles } from './styles'
import useVideoUploads from './use-video-uploads'
import VideoUploadList from './video-upload-list'
import {
  formatTurnaroundWithin,
  getCoachFirstName,
  getRequestPath,
  getVideoThumbnailUrl,
  GOLF_LEVEL_OPTIONS,
  MAX_VIDEOS,
} from './utils'

const MAX_TEXT_LENGTH = 500

export const validationSchema = yup.object().shape({
  golfLevel: yup.string().required('Choose your golf level'),
  struggle: yup
    .string()
    .trim()
    .required('Tell your coach what you are struggling with')
    .max(MAX_TEXT_LENGTH, `Keep this under ${MAX_TEXT_LENGTH} characters`),
  goal: yup
    .string()
    .trim()
    .required('Tell your coach what you want to achieve')
    .max(MAX_TEXT_LENGTH, `Keep this under ${MAX_TEXT_LENGTH} characters`),
})

const HINT_ID = 'virtual-coaching-upload-hint'

// `initialSelection` is a `VideoFilePicker` result (`{ files, errors }`) the
// golfer already picked elsewhere (the purchase confirmation page); it is
// handled once on mount exactly like a pick in this form.
const UploadForm = ({ request, initialUploads, initialSelection, branded }) => {
  const classes = useStyles()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [pickerError, setPickerError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const { coach, virtualCoaching } = request.lessonType || {}
  const coachFirstName = getCoachFirstName(coach)
  const turnaround = formatTurnaroundWithin(virtualCoaching?.turnaroundHours)

  const handleUploadEvent = useCallback(
    (name, attributes) => {
      trackVirtualCoachingStudentEvent(
        name === 'selected'
          ? VIRTUAL_COACHING_STUDENT_EVENTS.VIDEO_SELECTED
          : VIRTUAL_COACHING_STUDENT_EVENTS.VIDEO_UPLOAD_FAILED,
        { requestId: request.id, ...attributes },
      )
    },
    [request.id],
  )

  const uploads = useVideoUploads({
    onEvent: handleUploadEvent,
    initialItems: initialUploads,
  })
  const [
    submitVideos,
    { loading: isSubmitting },
  ] = useSubmitVirtualCoachingVideos()
  const { register, control, handleSubmit, errors, watch } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: {
      golfLevel: request.golfLevel || '',
      struggle: request.struggle || '',
      goal: request.goal || '',
    },
  })

  const handleSelect = ({ files, errors: fileErrors }) => {
    const messages = fileErrors.map(
      ({ file, message }) => `${file.name}: ${message}`,
    )
    const overflow = uploads.addFiles(files)
    if (overflow) messages.push(overflow)
    setPickerError(messages.length > 0 ? messages.join(' ') : null)
  }

  const handledInitialSelection = useRef(false)
  useEffect(() => {
    if (!initialSelection || handledInitialSelection.current) return
    handledInitialSelection.current = true
    handleSelect(initialSelection)
    // Once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = async values => {
    setSubmitError(null)
    const videoUrls = uploads.uploadedUrls
    const thumbnails = videoUrls.map(getVideoThumbnailUrl)

    try {
      await submitVideos({
        variables: {
          requestId: request.id,
          videoUrls,
          videoThumbnailUrls: thumbnails.every(Boolean)
            ? thumbnails
            : undefined,
          golfLevel: values.golfLevel,
          struggle: values.struggle.trim(),
          goal: values.goal.trim(),
        },
      })
    } catch (error) {
      const message =
        getErrorMessage(error) ||
        'We could not send your videos. Please try again.'
      setSubmitError(message)
      enqueueSnackbar(message, { variant: 'error' })
      trackVirtualCoachingStudentEvent(
        VIRTUAL_COACHING_STUDENT_EVENTS.VIDEOS_SUBMIT_FAILED,
        { requestId: request.id },
      )
      return
    }

    trackVirtualCoachingStudentEvent(
      VIRTUAL_COACHING_STUDENT_EVENTS.VIDEOS_SUBMITTED,
      { requestId: request.id, videoCount: videoUrls.length },
    )
    enqueueSnackbar(`Your videos were sent to ${coachFirstName}.`, {
      variant: 'success',
    })
    history.push(getRequestPath(request.id))
  }

  // The same rules as `validationSchema`, tracked as the golfer types so the
  // button enables only once the required answers are in. Field errors still
  // show on blur (`mode: 'onTouched'`), not on a pristine form.
  const { golfLevel, struggle, goal } = watch(['golfLevel', 'struggle', 'goal'])
  const hasAnswers = Boolean(golfLevel && struggle?.trim() && goal?.trim())
  const isBusy = isSubmitting
  const canSubmit = uploads.isReady && hasAnswers && !isBusy
  let uploadStatusMessage = ''
  if (uploads.isUploading) {
    uploadStatusMessage = 'Uploading your videos…'
  } else if (uploads.hasError) {
    uploadStatusMessage = 'One or more uploads failed.'
  } else if (uploads.isReady) {
    uploadStatusMessage = `${uploads.items.length} ${
      uploads.items.length === 1 ? 'video' : 'videos'
    } ready to send.`
  }

  let submitHint = null
  if (uploads.items.length === 0) {
    submitHint = 'Add at least one video to send.'
  } else if (uploads.isUploading) {
    submitHint = 'Wait for your videos to finish uploading.'
  } else if (uploads.hasError) {
    submitHint = 'Retry or remove the videos that failed to upload.'
  } else if (!golfLevel) {
    submitHint = 'Choose your golf level to send.'
  } else if (!struggle?.trim()) {
    submitHint = 'Tell your coach what you are struggling with to send.'
  } else if (!goal?.trim()) {
    submitHint = 'Tell your coach what you want to achieve to send.'
  }

  return (
    <Box
      component="form"
      className={classes.uploadForm}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <section className={classes.card} aria-labelledby="upload-videos-title">
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleBlock}>
            <Typography
              id="upload-videos-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              Your swing videos
            </Typography>
            <Typography className={classes.cardSubtitle}>
              {`Add 1 to ${MAX_VIDEOS} videos. MP4, MOV, or WebM, up to ${MAX_VIDEO_SIZE_MB}MB each.`}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.cardBody}>
          <Box className={classes.dropZone}>
            <Typography id={HINT_ID} variant="body2" className={classes.muted}>
              Record your swing, then choose the videos from your device.
            </Typography>
            <VideoFilePicker
              multiple
              onSelect={handleSelect}
              disabled={isBusy || uploads.isFull}
              label={
                uploads.items.length > 0 ? 'Add another video' : 'Choose videos'
              }
              startIcon={<FileUploadIcon />}
              inputTestId="virtual-coaching-upload-input"
              describedBy={HINT_ID}
              variant="contained"
            />
          </Box>
          {pickerError ? (
            <Typography
              color="error"
              variant="body2"
              role="alert"
              style={{ marginTop: 12 }}
            >
              {pickerError}
            </Typography>
          ) : null}
          <VideoUploadList
            items={uploads.items}
            onRemove={uploads.remove}
            onRetry={uploads.retry}
          />
          <div className={classes.srOnly} role="status" aria-live="polite">
            {uploadStatusMessage}
          </div>
        </Box>
      </section>

      <section className={classes.card} aria-labelledby="about-you-title">
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleBlock}>
            <Typography
              id="about-you-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              Tell {coachFirstName} about your game
            </Typography>
            <Typography className={classes.cardSubtitle}>
              This helps your coach focus their feedback.
            </Typography>
          </Box>
        </Box>
        <Box className={classes.cardBody}>
          <Controller
            name="golfLevel"
            control={control}
            render={({ name, value, onChange, onBlur, ref }) => (
              <TextField
                select
                name={name}
                id="virtual-coaching-golf-level"
                label="Golf level"
                variant="outlined"
                fullWidth
                required
                className={classes.formField}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                inputRef={ref}
                error={Boolean(errors.golfLevel)}
                helperText={errors.golfLevel?.message}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: selected =>
                    selected || (
                      <span className={classes.selectPlaceholder}>
                        Select your level
                      </span>
                    ),
                  MenuProps: {
                    classes: { paper: classes.selectMenuPaper },
                    // The select sets min-width to its own width; a zero
                    // width keeps the menu exactly that wide, so the long
                    // option descriptions wrap instead of widening it.
                    PaperProps: { style: { width: 0 } },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    getContentAnchorEl: null,
                  },
                }}
              >
                {GOLF_LEVEL_OPTIONS.map(
                  ({ value: optionValue, label, supportText }) => (
                    <MenuItem key={optionValue} value={optionValue}>
                      <Box>
                        {label}
                        <span className={classes.selectOptionSupport}>
                          {supportText}
                        </span>
                      </Box>
                    </MenuItem>
                  ),
                )}
              </TextField>
            )}
          />
          <TextField
            name="struggle"
            id="virtual-coaching-struggle"
            label="What are you struggling with?"
            placeholder="For example, slicing my driver or inconsistent contact with irons"
            variant="outlined"
            fullWidth
            required
            multiline
            minRows={2}
            className={classes.formField}
            inputRef={register}
            inputProps={{ maxLength: MAX_TEXT_LENGTH }}
            error={Boolean(errors.struggle)}
            helperText={errors.struggle?.message}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            name="goal"
            id="virtual-coaching-goal"
            label="What do you want to achieve?"
            placeholder="For example, hit it straighter and break 90"
            variant="outlined"
            fullWidth
            required
            multiline
            minRows={2}
            className={classes.formField}
            inputRef={register}
            inputProps={{ maxLength: MAX_TEXT_LENGTH }}
            error={Boolean(errors.goal)}
            helperText={errors.goal?.message}
            InputLabelProps={{ shrink: true }}
          />
          {turnaround ? (
            // Member pages use the /club tip (promo with the info icon, as on
            // the golfer intake form); the public success page keeps the
            // neutral brand tip of the checkout.
            <Box className={branded ? classes.signInTip : undefined}>
              <TipWell
                variant={branded ? 'neutral' : 'promo'}
                icon={branded ? undefined : InfoIcon}
                caption={`${coachFirstName} will review your swing ${turnaround} of receiving your videos.`}
              />
            </Box>
          ) : null}
        </Box>
      </section>

      {submitError ? (
        <Typography
          color="error"
          variant="body2"
          role="alert"
          style={{ marginBottom: 16 }}
        >
          {submitError}
        </Typography>
      ) : null}
      <Box className={classes.centerAction}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={!canSubmit}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          aria-describedby={
            submitHint ? 'virtual-coaching-submit-hint' : undefined
          }
        >
          {isSubmitting ? 'Sending…' : 'Share Videos'}
        </Button>
        {submitHint ? (
          <Typography
            id="virtual-coaching-submit-hint"
            variant="body2"
            className={classes.muted}
          >
            {submitHint}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

UploadForm.propTypes = {
  // True on the public success page, which keeps the brand tip styling.
  branded: PropTypes.bool,
  initialUploads: PropTypes.array,
  initialSelection: PropTypes.shape({
    files: PropTypes.array.isRequired,
    errors: PropTypes.array.isRequired,
  }),
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    golfLevel: PropTypes.string,
    struggle: PropTypes.string,
    goal: PropTypes.string,
    lessonType: PropTypes.object,
  }).isRequired,
}

export default UploadForm
